import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';

import { getInTransitDetail } from '@api/inventory';
import type { InTransitDetailItem } from '@api/inventory';
import { formatCount } from '@utils/currency';

interface InTransitBreakdownModalProps {
    isOpen: boolean;
    onClose: () => void;
    skuCode: string;
    productName: string;
    periodLabel: string;
    productId: string;
    periodStart: string;
    periodEnd: string;
}

const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const InTransitBreakdownModal: React.FC<InTransitBreakdownModalProps> = ({
    isOpen,
    onClose,
    skuCode,
    productName,
    periodLabel,
    productId,
    periodStart,
    periodEnd,
}) => {
    const [rows, setRows] = React.useState<InTransitDetailItem[]>([]);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() => {
        if (!isOpen) return;

        setLoading(true);
        getInTransitDetail({ productId, periodStart, periodEnd })
            .then((res) => {
                const sorted = [...res.data].sort((a, b) => {
                    if (!a.expectedDate) return 1;
                    if (!b.expectedDate) return -1;
                    return new Date(a.expectedDate).getTime() - new Date(b.expectedDate).getTime();
                });
                setRows(sorted);
            })
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [isOpen, productId, periodStart, periodEnd]);

    if (!isOpen) return null;

    const totalQty = rows.reduce((sum, r) => sum + r.quantity, 0);
    const inTransitRows = rows.filter((r) => r.state !== 'IN_PRODUCTION');
    const poQty = inTransitRows
        .filter((r) => (r.source ?? 'PO') === 'PO')
        .reduce((sum, r) => sum + r.quantity, 0);
    const stnQty = inTransitRows
        .filter((r) => r.source === 'STN')
        .reduce((sum, r) => sum + r.quantity, 0);
    const inProductionQty = rows
        .filter((r) => r.state === 'IN_PRODUCTION')
        .reduce((sum, r) => sum + r.quantity, 0);

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-900">
                            {periodLabel} Inward Plan
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {skuCode} – {productName}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="py-8 text-center text-sm text-slate-400">Loading...</div>
                    ) : rows.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400">No in-transit data for this period</div>
                    ) : (
                        <>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                        <th className="pb-3 pr-4">Type</th>
                                        <th className="pb-3 pr-4">Original Planned Date</th>
                                        <th className="pb-3 pr-4">Expected Date</th>
                                        <th className="pb-3 pr-4">Reference</th>
                                        <th className="pb-3 pr-4">Status</th>
                                        <th className="pb-3 text-right">Inward Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, i) => {
                                        const isStn = row.source === 'STN';
                                        const isProduction = row.state === 'IN_PRODUCTION';
                                        // "Production order" is the operator's name for a
                                        // work order, matching the replenishment drill-down.
                                        const label = isProduction
                                            ? row.source === 'WO'
                                                ? 'PROD'
                                                : 'IN PROD'
                                            : isStn
                                              ? 'STN'
                                              : 'PO';
                                        return (
                                        <tr key={`${row.purchaseOrderId}-${i}`} className="border-b border-slate-50">
                                            <td className="py-3 pr-4">
                                                <span
                                                    className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                                                        isProduction
                                                            ? 'bg-amber-100 text-amber-700'
                                                            : isStn
                                                              ? 'bg-violet-100 text-violet-700'
                                                              : 'bg-sky-100 text-sky-700'
                                                    }`}
                                                >
                                                    {label}
                                                </span>
                                            </td>
                                            <td className="py-3 pr-4 text-slate-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatDate(row.benchmarkDate)}
                                            </td>
                                            <td className="py-3 pr-4 text-slate-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatDate(row.expectedDate)}
                                            </td>
                                            <td className="py-3 pr-4 font-medium text-slate-900">
                                                {row.purchaseOrderId}
                                            </td>
                                            <td className="py-3 pr-4 text-slate-600">
                                                {row.status}
                                            </td>
                                            <td className="py-3 text-right font-semibold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {formatCount(Math.floor(row.quantity))}
                                            </td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    {(stnQty > 0 || inProductionQty > 0) && (
                                        <tr className="text-xs text-slate-500">
                                            <td colSpan={5} className="pt-3 pr-4 text-right">
                                                {[
                                                    poQty > 0 && `${formatCount(Math.floor(poQty))} in transit via PO`,
                                                    stnQty > 0 && `${formatCount(Math.floor(stnQty))} via STN`,
                                                    inProductionQty > 0 && `${formatCount(Math.floor(inProductionQty))} in production`,
                                                ]
                                                    .filter(Boolean)
                                                    .join(' · ')}
                                            </td>
                                            <td className="pt-3" />
                                        </tr>
                                    )}
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={5} className="py-3 pr-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">
                                            Total
                                        </td>
                                        <td className="py-3 text-right font-bold text-slate-900" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                            {formatCount(Math.floor(totalQty))}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default InTransitBreakdownModal;
